import { jwtDecode } from 'jwt-decode';

interface GoogleTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope: string;
  id_token?: string;
}

interface GoogleUserInfo {
  id: string;
  email: string;
  name: string;
  picture?: string;
  verified_email: boolean;
}

interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
  userInfo: GoogleUserInfo;
  idToken?: string;
}

class GoogleAuthService {
  private readonly CLIENT_ID = '164700727164-p9e9aao25dhaqih5r91ohdl2hkao4pb5.apps.googleusercontent.com';
  private readonly REDIRECT_URI = window.location.origin;
  /**
   * Compute redirect URI with a trailing slash to avoid mismatches
   */
  private getRedirectUri(): string {
    const origin = this.REDIRECT_URI.endsWith('/') ? this.REDIRECT_URI.slice(0, -1) : this.REDIRECT_URI;
    // Use a fixed callback path so it's easy to whitelist exactly
    return `${origin}/oauth/callback`;
  }
  private readonly SCOPES = 'openid email profile';
  private readonly STORAGE_KEY = 'google_auth_tokens';

  /**
   * Initiate Google OAuth flow with offline access (no PKCE, backend handles secret)
   */
  initiateAuth(): void {
    // Save current path to redirect back after auth
    sessionStorage.setItem('auth_redirect_path', window.location.pathname);
    
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', this.CLIENT_ID);
    const redirectUri = this.getRedirectUri();
    console.log('🔐 Google OAuth redirect_uri:', redirectUri);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', this.SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', this.generateState());

    window.location.href = authUrl.toString();
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleCallback(code: string): Promise<StoredTokens> {
    try {
      // Exchange authorization code for tokens
      const tokenResponse = await this.exchangeCodeForTokens(code);
      
      // Get user info
      const userInfo = await this.getUserInfo(tokenResponse.access_token);
      
      // Calculate expiration time
      const expiresAt = Date.now() + (tokenResponse.expires_in * 1000);
      
      const tokens: StoredTokens = {
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token,
        expiresAt,
        userInfo,
        idToken: tokenResponse.id_token,
      };
      
      // Store tokens
      this.storeTokens(tokens);
      
      return tokens;
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for access and refresh tokens via backend
   */
  private async exchangeCodeForTokens(code: string): Promise<GoogleTokenResponse> {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    const response = await fetch(`${apiUrl}/api/auth/google/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirect_uri: this.getRedirectUri(),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Token exchange failed: ${error.detail || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get user information from Google
   */
  private async getUserInfo(accessToken: string): Promise<GoogleUserInfo> {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get user info: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get current valid access token, refreshing if necessary
   */
  async getValidAccessToken(): Promise<string | null> {
    const tokens = this.getStoredTokens();
    
    if (!tokens) {
      return null;
    }

    // Check if token is expired (with 5-minute buffer)
    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    
    if (tokens.expiresAt - now < bufferTime) {
      // Token is expired or about to expire, try to refresh
      if (tokens.refreshToken) {
        try {
          const refreshedTokens = await this.refreshAccessToken(tokens.refreshToken);
          return refreshedTokens.accessToken;
        } catch (error) {
          console.error('Failed to refresh token:', error);
          this.clearTokens();
          return null;
        }
      } else {
        // No refresh token available, user needs to re-authenticate
        this.clearTokens();
        return null;
      }
    }

    return tokens.idToken || tokens.accessToken;
  }

  /**
   * Refresh access token using refresh token via backend
   */
  private async refreshAccessToken(refreshToken: string): Promise<StoredTokens> {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    const response = await fetch(`${apiUrl}/api/auth/google/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Token refresh failed: ${error.detail || response.statusText}`);
    }

    const tokenResponse = await response.json();
    
    // Get current stored tokens to preserve user info
    const currentTokens = this.getStoredTokens();
    
    const expiresAt = Date.now() + (tokenResponse.expires_in * 1000);
    
    const refreshedTokens: StoredTokens = {
      accessToken: tokenResponse.access_token,
      refreshToken: tokenResponse.refresh_token || refreshToken, // Keep existing if not provided
      expiresAt,
      userInfo: currentTokens?.userInfo || {} as GoogleUserInfo,
      idToken: tokenResponse.id_token || currentTokens?.idToken,
    };
    
    this.storeTokens(refreshedTokens);
    
    return refreshedTokens;
  }

  /**
   * Get stored tokens from localStorage
   */
  private getStoredTokens(): StoredTokens | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading stored tokens:', error);
      return null;
    }
  }

  /**
   * Store tokens in localStorage
   */
  private storeTokens(tokens: StoredTokens): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tokens));
    } catch (error) {
      console.error('Error storing tokens:', error);
    }
  }

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Get current user info
   */
  getCurrentUser(): GoogleUserInfo | null {
    const tokens = this.getStoredTokens();
    return tokens?.userInfo || null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const tokens = this.getStoredTokens();
    return tokens !== null && tokens.expiresAt > Date.now();
  }

  /**
   * Generate random state parameter for OAuth flow
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Legacy method: Handle ID token from GoogleLogin component
   * This is kept for backward compatibility but should be migrated away from
   */
  async handleIdToken(idToken: string): Promise<GoogleUserInfo> {
    try {
      const decoded = jwtDecode(idToken) as any;
      
      const userInfo: GoogleUserInfo = {
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        verified_email: decoded.email_verified
      };

      // Store as if it were an access token (but with 1-hour expiration)
      const expiresAt = Date.now() + (60 * 60 * 1000); // 1 hour
      
      const tokens: StoredTokens = {
        accessToken: idToken, // Using ID token as access token (not ideal)
        expiresAt,
        userInfo
      };
      
      this.storeTokens(tokens);
      
      return userInfo;
    } catch (error) {
      console.error('Error handling ID token:', error);
      throw error;
    }
  }
}

export const googleAuthService = new GoogleAuthService();
export type { GoogleUserInfo, StoredTokens };
